/**
 * MoveCardModal — dialog for moving a card to a different list/swimlane
 * without drag-and-drop. Useful for long boards where source and target
 * aren't in the same viewport.
 */

import * as React from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../ui/alert-dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { useActiveBoard } from '../../state/active-board-context';

export interface MoveCardModalProps {
  readonly boardId: string;
  readonly cardId: string;
  readonly cardTitle: string;
  readonly currentListId: string;
  readonly currentSwimlaneId: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onMoveConfirmed: (listId: string, swimlaneId: string) => void;
}

export function MoveCardModal({
  boardId,
  cardId,
  cardTitle,
  currentListId,
  currentSwimlaneId,
  open,
  onOpenChange,
  onMoveConfirmed,
}: MoveCardModalProps): React.ReactElement {
  const { state } = useActiveBoard();
  const [selectedListId, setSelectedListId] = React.useState(currentListId);
  const [selectedSwimlaneId, setSelectedSwimlaneId] = React.useState(currentSwimlaneId);

  // Reset selections when modal opens
  React.useEffect(() => {
    if (open) {
      setSelectedListId(currentListId);
      setSelectedSwimlaneId(currentSwimlaneId);
    }
  }, [open, currentListId, currentSwimlaneId]);

  const lists = state.lists;
  const swimlanes = state.swimlanes;
  const hasSwimlanes = swimlanes.length > 1;

  const isChanged = selectedListId !== currentListId || selectedSwimlaneId !== currentSwimlaneId;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Move Card</AlertDialogTitle>
          <AlertDialogDescription>
            Move &ldquo;{cardTitle}&rdquo; to a different list{hasSwimlanes ? ' or swimlane' : ''}.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-2">
          {/* List selector */}
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">
              List
            </label>
            <Select value={selectedListId} onValueChange={setSelectedListId}>
              <SelectTrigger className="w-full" aria-label="Select destination list">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {lists.map((list) => (
                  <SelectItem key={list.listId} value={list.listId}>
                    {list.title}{list.listId === currentListId ? ' (current)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Swimlane selector — only if board has multiple swimlanes */}
          {hasSwimlanes && (
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">
                Swimlane
              </label>
              <Select value={selectedSwimlaneId} onValueChange={setSelectedSwimlaneId}>
                <SelectTrigger className="w-full" aria-label="Select destination swimlane">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {swimlanes.map((sl) => (
                    <SelectItem key={sl.swimlaneId} value={sl.swimlaneId}>
                      {sl.title}{sl.swimlaneId === currentSwimlaneId ? ' (current)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={!isChanged}
            onClick={() => onMoveConfirmed(selectedListId, selectedSwimlaneId)}
          >
            Move
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
